import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  Brush,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { PIPEFITTER_CHART_COLORS } from '../constants/uiConstants';
import type { PipeDataGraphItem } from './PipeDataChart';
import { ChartBrushZoomControls, useChartBrushZoom } from './chartBrushZoom';

const OOR_CONFIG_LIMIT_COLOR = '#1d4ed8';

export interface PipeDataOorChartProps {
  pipeDataGraph: PipeDataGraphItem[];
  variant: 'id' | 'od';
  configLimit?: number | null;
  /** Less padding when embedded in a parent card */
  minimal?: boolean;
}

type OorChartRow = {
  pipeEndID: string;
  oor: number;
};

const parseNum = (v: string | number | null | undefined): number => {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  const n = parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : 0;
};

function computeYDomain(values: number[], configLimit?: number | null): [number, number] {
  const candidates = [
    ...values.filter((n) => Number.isFinite(n)),
    ...(configLimit != null && configLimit > 0 ? [configLimit] : []),
  ];
  if (!candidates.length) return [0, 1];

  const hi = Math.max(...candidates);
  const pad = Math.max(hi * 0.15, 0.25);
  const top = Math.ceil((hi + pad) * 2) / 2;
  return [0, Math.max(top, 0.5)];
}

function yAxisDecimals(domain: [number, number]): number {
  const span = domain[1] - domain[0];
  if (span <= 1) return 3;
  if (span <= 5) return 2;
  return 1;
}

export function hasGraphOorIdData(
  pipes: PipeDataGraphItem[],
  hasInnerRadiusData?: boolean
): boolean {
  if (hasInnerRadiusData === false) return false;
  return pipes.some((p) => p.ooR != null || p.oor != null);
}

export function hasGraphOorOdData(
  pipes: PipeDataGraphItem[],
  hasOuterRadiusData?: boolean
): boolean {
  if (hasOuterRadiusData === false) return false;
  return pipes.some((p) => p.ooROD != null || p.oorod != null);
}

const OorTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ value: number }>;
  label?: string;
}) => {
  if (!active || !payload?.length) return null;
  const value = payload[0]?.value ?? 0;

  return (
    <div
      style={{
        background: 'white',
        border: `1px solid ${PIPEFITTER_CHART_COLORS.border}`,
        borderRadius: 8,
        padding: '10px 14px',
        fontSize: 11,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}
    >
      <p style={{ color: 'black', marginBottom: 4, fontWeight: 600, wordBreak: 'break-all' }}>{label}</p>
      <p style={{ color: PIPEFITTER_CHART_COLORS.primary, margin: 0 }}>
        OoR: {value.toFixed(2)} mm
      </p>
    </div>
  );
};

export default function PipeDataOorChart({
  pipeDataGraph,
  variant,
  configLimit,
  minimal = false,
}: PipeDataOorChartProps) {
  const isId = variant === 'id';
  const seriesLabel = isId ? 'Out of Roundness (ID)' : 'Out of Roundness (OD)';
  const dimensionKey = isId ? 'ID' : 'OD';
  const hasConfigLimit = configLimit != null && configLimit > 0;
  const limitLegendLabel = hasConfigLimit
    ? `OoR Config Limit — ${dimensionKey} (${configLimit.toFixed(2)} mm)`
    : null;

  const data: OorChartRow[] = useMemo(
    () =>
      pipeDataGraph
        .map((d) => ({
          pipeEndID: String(d.pipeEndID ?? ''),
          oor: isId ? parseNum(d.ooR ?? d.oor) : parseNum(d.ooROD ?? d.oorod),
        }))
        .filter((row) => row.pipeEndID),
    [pipeDataGraph, isId]
  );

  const zoom = useChartBrushZoom(data.length);

  if (!data.length) return null;

  const yDomain = computeYDomain(
    data.map((row) => row.oor),
    configLimit
  );
  const yDecimals = yAxisDecimals(yDomain);
  const xTickInterval = zoom.visibleCount > 40 ? Math.ceil(zoom.visibleCount / 24) - 1 : 0;

  return (
    <div style={{ padding: minimal ? 0 : '10px 0' }}>
      <div
        style={{
          background: 'white',
          borderRadius: minimal ? 8 : 16,
          padding: minimal ? '8px 12px 8px' : '20px 16px 12px',
          width: '100%',
          border: `1px solid ${PIPEFITTER_CHART_COLORS.border}`,
        }}
      >
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2 px-1">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] text-text-grey">
            <span className="inline-flex items-center gap-1.5">
              <span
                className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
                style={{ background: PIPEFITTER_CHART_COLORS.primary }}
                aria-hidden
              />
              {seriesLabel}
            </span>
            {limitLegendLabel && (
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="inline-block h-0 w-5 shrink-0 border-t-2"
                  style={{ borderColor: OOR_CONFIG_LIMIT_COLOR }}
                  aria-hidden
                />
                {limitLegendLabel}
              </span>
            )}
          </div>
          <ChartBrushZoomControls
            canZoomIn={zoom.canZoomIn}
            canZoomOut={zoom.canZoomOut}
            isZoomed={zoom.isZoomed}
            onZoomIn={zoom.zoomIn}
            onZoomOut={zoom.zoomOut}
            onReset={zoom.resetZoom}
            visibleCount={zoom.visibleCount}
            totalCount={data.length}
          />
        </div>

        <ResponsiveContainer width="100%" height={minimal ? 340 : 380}>
          <BarChart data={data} margin={{ top: 8, right: 40, left: 8, bottom: 76 }} barCategoryGap="20%">
            <CartesianGrid strokeDasharray="3 3" stroke={PIPEFITTER_CHART_COLORS.border} vertical={false} />
            <XAxis
              dataKey="pipeEndID"
              padding={{ left: 8, right: 28 }}
              tick={{ fill: 'black', fontSize: 9 }}
              interval={xTickInterval}
              angle={-35}
              textAnchor="middle"
              height={64}
              tickLine={{ stroke: PIPEFITTER_CHART_COLORS.border }}
              axisLine={{ stroke: PIPEFITTER_CHART_COLORS.border }}
              label={{
                value: 'Pipe Profile Reference',
                position: 'insideBottom',
                offset: -8,
                fill: PIPEFITTER_CHART_COLORS.grayDark,
                fontSize: 10,
              }}
            />
            <YAxis
              domain={yDomain}
              tick={{ fill: 'black', fontSize: 10 }}
              tickFormatter={(v: number) => v.toFixed(yDecimals)}
              tickCount={6}
              tickLine={{ stroke: PIPEFITTER_CHART_COLORS.border }}
              axisLine={{ stroke: PIPEFITTER_CHART_COLORS.border }}
              label={{
                value: 'Out of Roundness (mm)',
                angle: -90,
                position: 'insideLeft',
                offset: 10,
                fill: 'black',
                fontSize: 10,
              }}
            />
            <Tooltip content={<OorTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
            {hasConfigLimit && (
              <ReferenceLine
                y={configLimit}
                stroke={OOR_CONFIG_LIMIT_COLOR}
                strokeWidth={2}
                ifOverflow="extendDomain"
              />
            )}
            <Bar
              dataKey="oor"
              name={seriesLabel}
              fill={PIPEFITTER_CHART_COLORS.primary}
              radius={[2, 2, 0, 0]}
              isAnimationActive={false}
            />
            <Brush
              dataKey="pipeEndID"
              height={26}
              stroke={PIPEFITTER_CHART_COLORS.primary}
              travellerWidth={10}
              startIndex={zoom.startIndex}
              endIndex={zoom.endIndex}
              onChange={zoom.onBrushChange}
              tickFormatter={() => ''}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
